import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBuilding,
    FaChartLine,
    FaCheckCircle,
    FaCogs,
    FaCoins, FaFileContract,
    FaGlobe,
    FaHandHoldingUsd,
    FaHeadset,
    FaLightbulb,
    FaLock,
    FaShieldAlt,
    FaUsers,
    FaUserTie,
    FaWallet
} from "react-icons/fa";
import { TiArrowRight, TiArrowUp } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import BookAConsultationCustomModal from "../components/BookAConsultationCustomModal";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from "./ui/GlassButton";
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  // Stats
  const stats = [
    { value: "21+", label: "Products", icon: <FaCogs /> },
    { value: "6", label: "Core Services", icon: <FaBuilding /> },
    { value: "24/7", label: "Support", icon: <FaHeadset /> },
    { value: "100%", label: "Secure", icon: <FaLock /> },
  ];

  // Core services with enhanced data
  const services = [
    {
      title: "Digital Wallets",
      description: "Enterprise-grade crypto wallet solutions with multi-signature security, automatic encrypted backups, and compatibility with major blockchains.",
      icon: <FaWallet />,
      color: "cyan",
      features: ["Multi-signature security", "Encrypted backups", "Multi-chain support"],
      products: ["Zero-Crypto-Wallet", "Zero-Wave"],
    },
    {
      title: "Asset Tokenisation",
      description: "Transform real-world assets into blockchain tokens. Enable fractional ownership with built-in compliance for regulated markets.",
      icon: <FaCoins />,
      color: "purple",
      features: ["Fractional ownership", "Compliance built-in", "Instant liquidity"],
      products: ["Zero-Tokeniser", "Zero-Dex"],
    },
    {
      title: "Smart Contracts",
      description: "Automated, transparent, and immutable agreements that eliminate intermediaries and ensure trustless execution.",
      icon: <FaFileContract />,
      color: "emerald",
      features: ["Automated execution", "Immutable & transparent", "Gas optimised"],
      products: ["Zero-Blockchain", "Zero-Core"],
    },
    {
      title: "Decentralised Lending",
      description: "Access global lending pools with competitive rates. Earn interest on idle assets or secure instant loans without paperwork.",
      icon: <FaHandHoldingUsd />,
      color: "orange",
      features: ["No intermediaries", "Global liquidity pools", "Instant approvals"],
      products: ["Zero-Lend", "Zero-Stake"],
    },
    {
      title: "Compliance Solutions",
      description: "Automated AML/KYC verification tools that seamlessly integrate with existing legal frameworks for regulatory adherence.",
      icon: <FaShieldAlt />,
      color: "pink",
      features: ["Automated KYC/AML", "Regulatory reporting", "Risk monitoring"],
      products: ["Zero-Comply", "Zero-Supervise"],
    },
    {
      title: "Blockchain Consulting",
      description: "Expert guidance on blockchain integration, custom strategy development, and comprehensive team training programmes.",
      icon: <FaUserTie />,
      color: "blue",
      features: ["Strategy development", "Implementation support", "Team training"],
      products: ["Zero-Insight", "Zero-Core"],
    },
  ];

  // Why choose us
  const whyChooseUs = [
    { icon: <FaGlobe />, title: "Global Reach", desc: "Operate across borders with multi-currency support" },
    { icon: <FaLock />, title: "Bank-Grade Security", desc: "256-bit encryption with multi-factor authentication" },
    { icon: <FaChartLine />, title: "Proven Results", desc: "Track record of successful implementations" },
    { icon: <FaUsers />, title: "Expert Team", desc: "Industry veterans with decade of blockchain experience" },
  ];

  // Color schemes
  const colorSchemes = {
    cyan: { gradient: "from-cyan-500/20 via-cyan-500/5 to-transparent", border: "hover:border-cyan-500/50", glow: "group-hover:shadow-cyan-500/25", icon: "text-cyan-400", bg: "bg-cyan-500" },
    purple: { gradient: "from-purple-500/20 via-purple-500/5 to-transparent", border: "hover:border-purple-500/50", glow: "group-hover:shadow-purple-500/25", icon: "text-purple-400", bg: "bg-purple-500" },
    emerald: { gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent", border: "hover:border-emerald-500/50", glow: "group-hover:shadow-emerald-500/25", icon: "text-emerald-400", bg: "bg-emerald-500" },
    orange: { gradient: "from-orange-500/20 via-orange-500/5 to-transparent", border: "hover:border-orange-500/50", glow: "group-hover:shadow-orange-500/25", icon: "text-orange-400", bg: "bg-orange-500" },
    pink: { gradient: "from-pink-500/20 via-pink-500/5 to-transparent", border: "hover:border-pink-500/50", glow: "group-hover:shadow-pink-500/25", icon: "text-pink-400", bg: "bg-pink-500" },
    blue: { gradient: "from-blue-500/20 via-blue-500/5 to-transparent", border: "hover:border-blue-500/50", glow: "group-hover:shadow-blue-500/25", icon: "text-blue-400", bg: "bg-blue-500" },
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/services-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="servicesHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Enterprise Services"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Enterprise Solutions · Technical Excellence · Strategic Support
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Introduction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Blockchain Solutions for Enterprise
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Comprehensive blockchain services designed to transform your business operations, 
            enhance security, and unlock new revenue streams.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl text-center group-hover:border-cyan-500/30 transition-all duration-500">
                  <div className="text-cyan-400 text-2xl mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Core Services Grid */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[0] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Our Core Services
            </span>
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-300 text-center mb-12 max-w-xl mx-auto"
          >
            End-to-end blockchain solutions tailored to your business needs
          </motion.p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const colors = colorSchemes[service.color];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`group relative ${colors.glow} hover:shadow-xl transition-all duration-500`}
                >
                  {/* Animated border */}
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-white/10 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className={`relative h-full bg-black/80 backdrop-blur-sm rounded-2xl p-6 border border-white/10 ${colors.border} transition-all duration-500 flex flex-col`}>
                    {/* Gradient glow */}
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-radial ${colors.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    {/* Icon */}
                    <div className={`relative z-10 w-14 h-14 rounded-2xl ${colors.bg}/20 flex items-center justify-center text-2xl ${colors.icon} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      {service.icon}
                    </div>
                    
                    {/* Title & Description */}
                    <h4 className="relative z-10 text-xl font-bold text-white mb-2">{service.title}</h4>
                    <p className="relative z-10 text-gray-300 text-sm leading-relaxed mb-4 flex-1">{service.description}</p>
                    
                    {/* Features */}
                    <div className="relative z-10 space-y-2 mb-4">
                      {service.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                          <FaCheckCircle className={`${colors.icon} text-xs`} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Related Products */}
                    <div className="relative z-10 mt-auto pt-4 border-t border-white/5">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Related Products:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {service.products.map((prod, i) => (
                          <span key={i} className={`px-2 py-1 rounded-full text-xs ${colors.bg}/10 ${colors.icon} border border-white/5`}>
                            {prod}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
          <div className="absolute inset-0 bg-black/80" />
          
          <div className="relative z-10 py-16 px-8">
            <h3 className="text-3xl font-bold text-center mb-12 text-white">
              Why Choose Zero-Blockchain?
            </h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl text-cyan-400 mx-auto mb-4">
                    {item.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-gray-300 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <h3 className="text-3xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              How We Work
            </span>
          </h3>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Discovery", desc: "We analyse your business needs and identify blockchain opportunities" },
              { step: "02", title: "Strategy", desc: "Custom roadmap development tailored to your goals and timeline" },
              { step: "03", title: "Implementation", desc: "Expert deployment with rigorous testing and security audits" },
              { step: "04", title: "Support", desc: "Ongoing maintenance, updates, and 24/7 technical assistance" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-emerald-500/30 to-cyan-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
                <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 group-hover:border-emerald-500/30 transition-all duration-500">
                  <div className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                    {item.step}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-gray-300 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          <div className="relative z-10 py-16 px-8 text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm mb-6"
            >
              <FaLightbulb /> Free Consultation Available
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Discover how our expert blockchain services can help you automate processes, 
              reduce costs, and unlock new opportunities. Schedule a free consultation today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GlassButton
                onClick={handleOpenModal}
                className="px-8 py-4"
                variant="cyan"
                size="lg"
              >
                <div className="flex items-center gap-2">
                   <TiArrowRight /> Book a Consultation
                </div>
              </GlassButton>
              <GlassButton
                onClick={() => navigate('/products')}
                className="px-8 py-4"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-2">
                   <TiArrowRight /> Explore Products
                </div>
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      <BookAConsultationCustomModal isOpen={isModalOpen} onClose={handleCloseModal} />

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Services;